import type { PresenceUser } from '@/services/estimates-service'

/**
 * "Someone else is in this estimate right now."
 *
 * The builder saves each field a second or so after it is typed, so two people
 * in the same estimate silently overwrite each other — the last save wins and
 * neither is told. Presence was already tracked and shown as small avatars in
 * the header, but a crew hit this repeatedly without noticing them, so the
 * warning is stated in full across the top of the page instead.
 *
 * It is not a lock: both people can still type. It tells them to sort it out
 * between themselves, which is what the requester asked for.
 */

/** "Dana Cole", "Dana Cole and Kyle Ramirez", "Dana Cole, Kyle Ramirez and 2 others". */
export function namesSentence(names: string[]): string {
  const clean = names.map((n) => n.trim()).filter(Boolean)
  if (clean.length === 0) return ''
  if (clean.length === 1) return clean[0]
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`
  const rest = clean.length - 2
  return `${clean[0]}, ${clean[1]} and ${rest} other${rest === 1 ? '' : 's'}`
}

export function ConcurrentEditorsBanner({ others }: { others: PresenceUser[] }) {
  if (others.length === 0) return null

  const who = namesSentence(others.map((u) => u.displayName))
  const isPlural = others.length > 1

  return (
    <div className="ce-banner" role="status">
      <div className="ce-banner-icon" aria-hidden="true">
        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="ce-banner-text">
        <strong>{who} {isPlural ? 'are' : 'is'} also in this estimate right now.</strong>{' '}
        Changes save as you type, so if you edit the same thing, whoever types last wins — check
        with them before changing the same section.
      </div>
    </div>
  )
}
